import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { trpc } from '../lib/trpc';
import { showToast } from '../utils/toast';

export const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationComplete, setVerificationComplete] = useState(false);

  // Get email from location state (passed from registration) or URL params
  const email = (location.state as { email?: string } | null)?.email || searchParams.get('email') || '';

  // tRPC mutations and queries
  const resendVerificationMutation = trpc.auth.resendVerification.useMutation();
  const verifyEmailMutation = trpc.auth.verifyEmail.useMutation();
  const utils = trpc.useUtils();

  // Handle verification callback (when user clicks link in email)
  useEffect(() => {
    const token = searchParams.get('token');
    const emailParam = searchParams.get('email');

    const handleVerification = async (emailAddress: string, token: string) => {
      setIsVerifying(true);

      try {
        const result = await verifyEmailMutation.mutateAsync({
          email: emailAddress,
          token,
        });

        showToast(result.message, 'success');
        setVerificationComplete(true);

        // Invalidate user query to get fresh data
        await utils.user.getMe.invalidate();

        // Fetch updated user data to check onboarding status
        const userData = await utils.user.getMe.fetch();

        // Redirect based on onboarding status after a short delay
        setTimeout(() => {
          if (userData.onboardingComplete) {
            navigate('/');
          } else {
            navigate('/onboarding');
          }
        }, 2000);
      } catch (error: unknown) {
        if (error instanceof Error) {
          showToast(error.message, 'error');
        } else {
          showToast('Failed to verify email', 'error');
        }
      } finally {
        setIsVerifying(false);
      }
    };

    if (token && emailParam) {
      handleVerification(emailParam, token);
    }
  }, [searchParams, verifyEmailMutation, navigate, utils]);

  const handleResendEmail = async () => {
    if (!email) {
      showToast('Email address is required', 'error');
      return;
    }

    try {
      const result = await resendVerificationMutation.mutateAsync({
        email,
      });

      showToast(result.message, 'success');
    } catch (error: unknown) {
      if (error instanceof Error) {
        showToast(error.message, 'error');
      } else {
        showToast('Failed to resend verification email', 'error');
      }
    }
  };

  // If verifying from callback
  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Card variant="elevated" padding="lg">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
                <svg
                  className="w-8 h-8 text-primary-600 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying your email...</h2>
              <p className="text-gray-600">Please wait while we verify your email address.</p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // If verification is complete
  if (verificationComplete) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Card variant="elevated" padding="lg">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h2>
              <p className="text-gray-600 mb-4">
                Your email has been successfully verified. Redirecting you now...
              </p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Default view: waiting for verification
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Check Your Email</h1>
          <p className="text-gray-600">
            We've sent a verification link to your email address
          </p>
        </div>

        <Card variant="elevated" padding="lg">
          <div className="text-center">
            {/* Email Icon */}
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
              <svg
                className="w-8 h-8 text-primary-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>

            {email && (
              <p className="text-sm text-gray-600 mb-6">
                We sent a verification link to <strong>{email}</strong>
              </p>
            )}

            <div className="space-y-4 mb-6">
              <p className="text-gray-700">
                Click the link in the email to verify your account and continue to WineryXplorer.
              </p>
              <p className="text-sm text-gray-600">
                The link will expire in 24 hours.
              </p>
            </div>

            {/* Resend Button */}
            <Button
              variant="outline"
              size="lg"
              fullWidth
              onClick={handleResendEmail}
              loading={resendVerificationMutation.isPending}
              disabled={resendVerificationMutation.isPending || !email}
            >
              Resend Verification Email
            </Button>

            {/* Help Text */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-2">Didn't receive the email?</p>
              <ul className="text-sm text-gray-600 text-left space-y-1">
                <li>• Check your spam or junk folder</li>
                <li>• Make sure you entered the correct email address</li>
                <li>• Wait a few minutes and try resending</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Back to Login */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/login')}
            className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};
